/**
 * Host interface (M2.4, FR-9.5/9.6 — "one goal = one action").
 *
 * Rebuilt FRESH against the shared command set — deliberately NOT a port of the
 * legacy `HostQuestionView`, whose dual scoring paths are exactly what we're
 * leaving behind. Every host goal maps to exactly ONE control, shown only when the
 * command is legal (legality derived once in `host-actions.ts`, mirroring the
 * server guards). Judging a buzzed-in answer applies the score in that single
 * action — there is no separate scoring step or panel.
 *
 * Recovery controls (reopen_buzzer, reset_timer — FR-4.9) are grouped in a
 * visually distinct box, each a single button.
 */

import { gamePhaseOf, gamePhaseOfOrNull } from '@jeopardy/shared';
import { useEffect, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { requestSpectatorTicket } from '../net/game-api';
import { requestHostTicketForGame } from '../net/pack-api';
import { clearHostSession, loadHostSession } from '../net/host-session';
import { loadRateCluesEnabled } from '../net/clue-rating-prefs';
import { loadIdentity } from '../net/auth';
import { InsufficientCreditsNotice } from '../ui/CreditBalance';
import { IS_PROD_DEPLOY } from '../config';
import { useGameConnection } from '../net/use-game';
import { AppHeader } from '../ui/AppHeader';
import { HIDDEN_PERIL, DOUBLE_PERIL, FINAL_PERIL } from '../ui/brand';
import { Board } from '../ui/Board';
import { ClueRating } from '../ui/ClueRating';
import { CluePanel } from '../ui/CluePanel';
import { CreateGame } from '../ui/CreateGame';
import { parseGrantedCredits, PurchaseSuccess } from '../ui/PurchaseSuccess';
import { HostGate } from '../ui/HostGate';
import { homeGuard } from '../ui/leave-guard';
import { GameOver } from '../ui/GameOver';
import { PostGameFeedback } from '../ui/PostGameFeedback';
import { Lobby } from '../ui/Lobby';
import { GenerationStatus, GenerationFailedBanner } from '../ui/GenerationStatus';
import { JourneyStepper } from '../ui/JourneyStepper';
import { journeyStep } from '../game/journey';
import { JoinShare } from '../ui/JoinShare';
import { PowerUpHostLog, usePowerupActivityLog } from '../ui/PowerUpHostLog';
import { Scoreboard } from '../ui/Scoreboard';
import { TimerView } from '../ui/TimerView';
import { presentationUrl } from '../net/join-url';
import { hostActionAvailability } from '../game/host-actions';
import {
  categoryNameForClue,
  currentClue,
  finalRevealResolvesAt,
  finalRevealResponse,
  isActivePlayPhase,
  isClueOpen,
  isDailyDoubleAwaitingWager,
  nextFinalJudgePlayer,
} from '../game/selectors';
import { isAutoHost } from '../game/voice-selectors';
import { FinalReveal } from '../ui/FinalReveal';
import { useDocumentHead } from '../seo/useDocumentHead';
import { headModelFor } from '../seo/route-seo';

export function HostPage(): React.JSX.Element {
  // Per-route SEO (#241, FR-15.4): self-canonical + host-intent head so this surface is
  // no longer deduped against the landing (`canonical=/`).
  useDocumentHead(headModelFor('/host'));
  const [ticket, setTicket] = useState<string | null>(null);
  const [joinCode, setJoinCode] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Whether we're still trying to RESTORE a persisted host session on load (prod).
  // Until this settles we must NOT show the pack lobby, or a reload would flash the
  // "pick a pack" screen before the restore completes.
  const [restoring, setRestoring] = useState(IS_PROD_DEPLOY);
  // The host's signed guest token — proves identity for the credit balance read +
  // code redemption (#44). Read once from the persisted identity (a guest is minted
  // on first use); absent only in dev with no identity, where credits don't apply.
  const [guestToken] = useState<string | null>(() => loadIdentity()?.guestToken ?? null);
  // Whether the host is a registered account — gates the buy-credits pack picker (#125,
  // D-125.5). Paid credits bind to a registered account; a guest sees a sign-in steer.
  const [registered] = useState<boolean>(() => {
    const id = loadIdentity();
    return id !== null && !id.isGuest;
  });
  // Checkout return confirmation (#197, FR-12.6.1). The shared PurchaseSuccess banner
  // renders + self-clears `?checkout`/`credits`; we capture the server-stamped granted
  // amount ONCE (before it clears) to tie the create-screen CreditBalance to the purchase
  // with a "+N" chip. Undefined on a normal visit → no highlight.
  const [searchParams] = useSearchParams();
  const [successDelta] = useState(() =>
    searchParams.get('checkout') === 'success'
      ? parseGrantedCredits(searchParams.get('credits'))
      : undefined,
  );

  // Launched from an SEO trivia page (#295): the `/api/trivia/games` route already
  // created the game + minted this host ticket, handed here via navigation state.
  // Seed the ticket directly so the host lands ON the board — no /ticket round trip,
  // no pack lobby, no session restore.
  const location = useLocation();
  useEffect(() => {
    const handoff = location.state as { hostTicket?: string; joinCode?: string } | null;
    if (handoff?.hostTicket) {
      setTicket(handoff.hostTicket);
      if (handoff.joinCode) setJoinCode(handoff.joinCode);
      setRestoring(false);
    }
  }, [location.state]);

  // In DEV mode there is no pack store, so auto-acquire a ticket for the single demo
  // game (the M2 behavior the e2e drives).
  useEffect(() => {
    if (IS_PROD_DEPLOY) return; // deployed: PackLobby / session-restore drive it
    let cancelled = false;
    void requestSpectatorTicket('host')
      .then((t) => {
        if (!cancelled) setTicket(t.ticket);
      })
      .catch((e) => {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Could not start host session');
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // DEPLOYED mode: on load, RESTORE a persisted host session (a tab reload keeps the
  // host in their game instead of bouncing to the lobby). The game handle is
  // persisted at create time; here we RE-MINT a fresh host ticket via the
  // authenticated `/ticket` path (M11.5.1) — which carries the creator's persisted
  // guest token, so only the original creator can restore. A 403/404 (no longer the
  // host / game gone) or any failure clears the stale handle and falls through to the
  // pack lobby. With no persisted session we go straight to the lobby.
  useEffect(() => {
    if (!IS_PROD_DEPLOY) return;
    // A fresh trivia handoff (#295) wins over any stale persisted session: the seed
    // effect above already set the ticket for the just-launched board, so restoring an
    // OLD host session here would race and connect to the wrong game (the "hang, then
    // reload works" symptom). Skip restore entirely when we were handed a ticket.
    if ((location.state as { hostTicket?: string } | null)?.hostTicket) {
      setRestoring(false);
      return;
    }
    const session = loadHostSession();
    if (!session) {
      setRestoring(false);
      return;
    }
    let cancelled = false;
    void requestHostTicketForGame(session.gameId)
      .then((t) => {
        if (cancelled) return;
        setTicket(t);
        setJoinCode(session.joinCode);
      })
      .catch(() => {
        // Game ended, reaped, or this browser is no longer the host — drop the stale
        // handle and let the host create a new game.
        if (!cancelled) clearHostSession();
      })
      .finally(() => {
        if (!cancelled) setRestoring(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const { snapshot, status, send } = useGameConnection(ticket);
  const state = snapshot.state;

  // Once the game is over, drop the persisted host session so a later reload returns
  // to the lobby to start a fresh game rather than restoring into a finished one
  // (which may also be reaped server-side — a re-mint would then 404 anyway).
  useEffect(() => {
    if (IS_PROD_DEPLOY && gamePhaseOfOrNull(state) === 'GAME_OVER') clearHostSession();
  }, [gamePhaseOfOrNull(state)]);

  const actions = hostActionAvailability(state);
  const clue = currentClue(state);
  const clueOpen = isClueOpen(state);
  const activePlayer = state?.players.find(
    (p) => p.id === state.jeopardy.clueState?.activePlayerId,
  );
  const ddAwaitingWager = isDailyDoubleAwaitingWager(state);
  const ddUncoverer = state?.players.find(
    (p) => p.id === state?.jeopardy.clueState?.dailyDoublePlayerId,
  );
  const finalJudgePlayer = nextFinalJudgePlayer(state);

  // #232 (Game Night only): a read-only power-up activity feed so a HUMAN host stays
  // oriented (the app resolves all state — the host never manages it). The log
  // accumulates from the store's most-recent play/grant stream. No-op in Classic.
  const powerupsOn = Boolean(state?.jeopardy.powerups);
  const powerupLog = usePowerupActivityLog(
    state?.players ?? [],
    snapshot.lastPowerupPlayed,
    snapshot.lastPowerupGranted,
  );
  // A correct ruling on the buzz winner applies 2× when they armed Double Up (never on a
  // Daily Double, where Double Up is disabled). Purely a judging hint — the server owns
  // the multiplier; the host just presses the same Correct button.
  const doubleUpWinner =
    powerupsOn &&
    activePlayer !== undefined &&
    state?.jeopardy.clueState?.mode !== 'DAILY_DOUBLE' &&
    (state?.jeopardy.clueState?.doubleUpBy?.includes(activePlayer.id) ?? false);
  const doubleUpAward = doubleUpWinner && clue ? clue.value * 2 : 0;

  // The host's step in the create→play journey (#150, FR-9.11). Null during active
  // play / game-over (tracker hidden) and while a persisted session is still restoring
  // (we don't yet know whether we're creating or resuming — don't flash "Create Game").
  const journeyCurrent = journeyStep(ticket !== null, state);
  const showStepper =
    journeyCurrent !== null && !(IS_PROD_DEPLOY && !ticket && restoring);

  return (
    <div className="app-shell">
      {/* Mid-game, confirm before leaving so the host doesn't drop the room by a
          mis-tap (FR-9.8 / D-F8.2). The game keeps running and restores on return. */}
      <AppHeader
        role="Host"
        status={status}
        onHomeIntercept={homeGuard(
          isActivePlayPhase(state),
          'Leave this game? It keeps running and you can return to it from home.',
        )}
      />

      {/* Post-purchase confirmation (#197, FR-12.6.1) — the surface the issue names
          (`/host?checkout=success`). Shown above the create/lobby content so a purchase off
          the create screen confirms in place; self-clears its params so a reload doesn't
          re-assert it. Unconditional (keys off the URL), so it works before any game exists. */}
      <PurchaseSuccess />

      {error && <div className="error-text">{error}</div>}
      {/* A paid-path start denied for lack of credits (#44, FR-12.3) gets a dedicated
          recovery: a clear message + the SINGLE "redeem a code" action (FR-9.5). The
          game stays in the lobby, so the host redeems then re-uses the existing start
          control. Any other server error keeps the plain message. */}
      {snapshot.lastError &&
        (snapshot.lastError.code === 'INSUFFICIENT_CREDITS' && guestToken ? (
          <InsufficientCreditsNotice guestToken={guestToken} />
        ) : (
          <div className="error-text" data-testid="server-error">
            {snapshot.lastError.message}
          </div>
        ))}

      {/* Journey progress tracker (#150, FR-9.11): create screen + pre-play phases
          (LOBBY/GENERATING); hidden during active play, game-over, and mid-restore. */}
      {showStepper && journeyCurrent && <JourneyStepper current={journeyCurrent} />}

      {/* Deployed: while restoring a persisted host session on load, wait — don't
          flash the lobby (a reload should land the host back in their game). */}
      {IS_PROD_DEPLOY && !ticket && restoring && (
        <p className="muted" data-testid="host-restoring">
          Restoring your game…
        </p>
      )}

      {/* Deployed: no game (and none to restore) → show the pack lobby (upload/pick a
          pack, create a game). It mints the host ticket + the join code players use
          (M8.4) and persists the session so the next reload restores it. Hosting
          requires a registered account (#171, FR-12.7): HostGate presents the sign-in
          panel to a guest and only renders the create flow once registered (a no-op
          when auth is unconfigured — degrade). */}
      {IS_PROD_DEPLOY && !ticket && !restoring && (
        <HostGate>
          <CreateGame
            onGameReady={(t, code) => {
              setTicket(t);
              setJoinCode(code);
            }}
            guestToken={guestToken}
            registered={registered}
            successDelta={successDelta}
          />
        </HostGate>
      )}

      {/* Once a game exists, show the join code as a scannable QR + copyable link so
          players can join THIS game (M8 join-by-code; QR share FR-2.9), plus the
          shared-screen deep link the host opens on the TV (FR-9.10, #51) — it carries
          THIS game's code so the presentation binds to this game, not the demo one. */}
      {IS_PROD_DEPLOY && ticket && joinCode && (
        <div className="join-code-banner" data-testid="host-join-code">
          <JoinShare joinCode={joinCode} variant="compact" />
          <a
            className="join-code-banner__presentation-link"
            data-testid="host-presentation-link"
            href={presentationUrl(joinCode)}
            target="_blank"
            rel="noopener noreferrer"
          >
            Open the presentation screen
          </a>
        </div>
      )}

      {!IS_PROD_DEPLOY && !snapshot.hasState && !error && (
        <p className="muted">Starting host session…</p>
      )}

      {/* Generation failed (M12, FR-9.9): the phase has returned to LOBBY. The host is
          the only role offered the recovery action (retry). */}
      {snapshot.lastGenerationFailure && gamePhaseOfOrNull(state) === 'LOBBY' && (
        <GenerationFailedBanner
          code={snapshot.lastGenerationFailure.code}
          message={snapshot.lastGenerationFailure.message}
          onRetry={
            actions.startGeneration ? () => send({ type: 'start_generation' }) : undefined
          }
        />
      )}

      {/* Pre-play lobby (M12): roster + live category wishes + the ONE host action. */}
      {state &&
        (gamePhaseOf(state) === 'LOBBY' || gamePhaseOf(state) === 'GENERATING') && (
          <div className="stack">
            <Lobby players={state.players} mode={state.mode} lobby={state.lobby} />
            {/* The proactive credit balance + get-more-credits slot now lives on the CREATE
              step (#171 review): the host sees it before inviting players, not here in the
              lobby. If a paid-path start is still denied for lack of credits, the
              INSUFFICIENT_CREDITS recovery above is the safety net (FR-12.3). */}
            {gamePhaseOf(state) === 'GENERATING' && state.generation && (
              <GenerationStatus generation={state.generation} />
            )}
            <div className="controls">
              {actions.startGame && (
                <button
                  type="button"
                  className="btn btn--primary"
                  data-testid="start-game"
                  onClick={() => send({ type: 'start_game' })}
                >
                  Start game
                </button>
              )}
              {actions.startGeneration && (
                <button
                  type="button"
                  className="btn btn--primary"
                  data-testid="start-generation"
                  onClick={() => send({ type: 'start_generation' })}
                >
                  Generate &amp; start
                </button>
              )}
              {gamePhaseOf(state) === 'LOBBY' &&
                state.mode === 'DYNAMIC' &&
                !actions.startGeneration && (
                  <p className="muted" data-testid="awaiting-prefs">
                    Waiting for at least one player to submit categories…
                  </p>
                )}
              {actions.cancelGeneration && (
                <button
                  type="button"
                  className="btn btn--danger"
                  data-testid="cancel-generation"
                  onClick={() => send({ type: 'cancel_generation' })}
                >
                  Cancel generation
                </button>
              )}
            </div>
            {actions.endGame && (
              <button
                type="button"
                className="btn btn--danger"
                data-testid="end-game"
                onClick={() => send({ type: 'end_game' })}
              >
                End game
              </button>
            )}
          </div>
        )}

      {state && gamePhaseOf(state) !== 'LOBBY' && gamePhaseOf(state) !== 'GENERATING' && (
        <div className="layout-two-col">
          <div className="stack">
            {gamePhaseOf(state) === 'GAME_OVER' ? (
              <>
                <GameOver
                  players={state.players}
                  gameId={IS_PROD_DEPLOY ? state.gameId : undefined}
                  finalOutcome={state.jeopardy.finalOutcome}
                />
                {/* Post-game rating (#200, FR-16.1 / DI-D): the host sees it too, not the
                    presentation screen. Once per game, skippable. */}
                <PostGameFeedback gameId={state.gameId} />
              </>
            ) : actions.finalJudge && finalJudgePlayer && state.jeopardy.final ? (
              <>
                {/* Final Jeopardy judging — one player at a time, lowest first. */}
                <CluePanel
                  value={0}
                  prompt={state.jeopardy.packSnapshot.finalJeopardy.prompt}
                  categoryName={state.jeopardy.final.category}
                  response={state.jeopardy.packSnapshot.finalJeopardy.response}
                />
                <div className="clue-panel" data-testid="final-reveal">
                  <div className="clue-panel__value">Judging {finalJudgePlayer.name}</div>
                  <div className="clue-panel__prompt" data-testid="final-player-answer">
                    {state.jeopardy.final.answers[finalJudgePlayer.id] ??
                      '(no answer submitted)'}
                  </div>
                  <div className="clue-panel__response">
                    Wager: ${state.jeopardy.final.wagers[finalJudgePlayer.id] ?? 0}
                  </div>
                </div>
                <div className="controls">
                  <button
                    type="button"
                    className="btn btn--correct"
                    data-testid="final-judge-correct"
                    onClick={() =>
                      send({
                        type: 'final_judge',
                        playerId: finalJudgePlayer.id,
                        correct: true,
                      })
                    }
                  >
                    Correct (+${state.jeopardy.final.wagers[finalJudgePlayer.id] ?? 0})
                  </button>
                  <button
                    type="button"
                    className="btn btn--incorrect"
                    data-testid="final-judge-incorrect"
                    onClick={() =>
                      send({
                        type: 'final_judge',
                        playerId: finalJudgePlayer.id,
                        correct: false,
                      })
                    }
                  >
                    Incorrect (−${state.jeopardy.final.wagers[finalJudgePlayer.id] ?? 0})
                  </button>
                </div>
              </>
            ) : gamePhaseOf(state) === 'FINAL_WAGER' ||
              gamePhaseOf(state) === 'FINAL_CLUE' ? (
              <div className="clue-panel" data-testid="final-status">
                <div className="clue-panel__value">
                  {FINAL_PERIL} — {state.jeopardy.final?.category}
                </div>
                <div className="clue-panel__prompt">
                  {gamePhaseOf(state) === 'FINAL_WAGER'
                    ? 'Waiting for players to submit their secret wagers…'
                    : 'Players are typing their secret answers…'}
                </div>
                {gamePhaseOf(state) === 'FINAL_CLUE' && (
                  <div className="clue-panel__response">
                    {state.jeopardy.packSnapshot.finalJeopardy.prompt}
                    <div className="clue-panel__response-label">
                      Correct: {state.jeopardy.packSnapshot.finalJeopardy.response}
                    </div>
                  </div>
                )}
              </div>
            ) : gamePhaseOf(state) === 'FINAL_REVEAL' && finalRevealResponse(state) ? (
              // #205 (FR-6.6): the last player is judged — linger on the revealed
              // correct response with a countdown before GAME_OVER (no host action).
              <FinalReveal
                response={finalRevealResponse(state)!}
                category={state.jeopardy.final?.category}
                resolvesAtEpochMs={finalRevealResolvesAt(state)}
              />
            ) : clueOpen && clue ? (
              <>
                <CluePanel
                  value={clue.value}
                  prompt={clue.prompt}
                  categoryName={categoryNameForClue(state, clue.id)}
                  response={clue.response}
                />
                {state.timer.clueId && <TimerView timer={state.timer} />}

                {ddAwaitingWager && (
                  <p className="muted" data-testid="awaiting-wager">
                    {HIDDEN_PERIL} — waiting for{' '}
                    <strong>{ddUncoverer?.name ?? 'the player'}</strong> to submit a
                    wager. Read the clue once the wager is in.
                  </p>
                )}
                {activePlayer && (
                  <p data-testid="answering-player">
                    <strong>{activePlayer.name}</strong>
                    {state.jeopardy.clueState?.mode === 'DAILY_DOUBLE'
                      ? ` answers the ${HIDDEN_PERIL} — judge it:`
                      : ' buzzed in — judge their answer:'}
                  </p>
                )}

                {/* Normal-play controls: exactly one per command, shown only when legal. */}
                <div className="controls">
                  {actions.openBuzzer && (
                    <button
                      type="button"
                      className="btn btn--primary"
                      data-testid="open-buzzer"
                      onClick={() => send({ type: 'open_buzzer' })}
                    >
                      {/* #350: a DD has no buzzer — the same command opens the
                          uncoverer's answer window, so label it honestly. */}
                      {state.jeopardy.clueState?.mode === 'DAILY_DOUBLE'
                        ? 'Open answer'
                        : 'Open buzzer'}
                    </button>
                  )}
                  {actions.judge && (
                    <>
                      <button
                        type="button"
                        className="btn btn--correct"
                        data-testid="judge-correct"
                        onClick={() => send({ type: 'judge', correct: true })}
                      >
                        Correct
                        {doubleUpWinner && (
                          <small className="btn__hint" data-testid="judge-doubleup-hint">
                            applies ⏫ 2× → +${doubleUpAward.toLocaleString('en-US')}
                          </small>
                        )}
                      </button>
                      <button
                        type="button"
                        className="btn btn--incorrect"
                        data-testid="judge-incorrect"
                        onClick={() => send({ type: 'judge', correct: false })}
                      >
                        Incorrect
                      </button>
                    </>
                  )}
                  {actions.pauseTimer && (
                    <button
                      type="button"
                      className="btn"
                      data-testid="pause-timer"
                      onClick={() => send({ type: 'pause_timer' })}
                    >
                      Pause timer
                    </button>
                  )}
                  {actions.resumeTimer && (
                    <button
                      type="button"
                      className="btn"
                      data-testid="resume-timer"
                      onClick={() => send({ type: 'resume_timer' })}
                    >
                      Resume timer
                    </button>
                  )}
                </div>

                {/* Recovery controls (FR-4.9): grouped + visually distinct. */}
                <div className="controls__group controls__group--recovery">
                  <span className="controls__group-label">Recovery</span>
                  {actions.reopenBuzzer && (
                    <button
                      type="button"
                      className="btn btn--recovery"
                      data-testid="reopen-buzzer"
                      onClick={() => send({ type: 'reopen_buzzer' })}
                    >
                      Reopen buzzer (undo accidental buzz)
                    </button>
                  )}
                  {actions.dismissClue && (
                    <button
                      type="button"
                      className="btn btn--recovery"
                      data-testid="dismiss-clue"
                      onClick={() => send({ type: 'dismiss_clue' })}
                    >
                      Dismiss clue (nobody gets this)
                    </button>
                  )}
                  {actions.resetTimer && (
                    <button
                      type="button"
                      className="btn btn--recovery"
                      data-testid="reset-timer"
                      onClick={() => send({ type: 'reset_timer' })}
                    >
                      Reset timer
                    </button>
                  )}
                </div>
              </>
            ) : (
              <>
                <p className="muted">
                  {actions.canSelectClue ? 'Select a clue from the board.' : 'Waiting…'}
                </p>
                <Board
                  board={state.jeopardy.board}
                  cluesById={state.jeopardy.packSnapshot.cluesById}
                  onSelectClue={
                    actions.canSelectClue
                      ? (clueId) => send({ type: 'select_clue', clueId })
                      : undefined
                  }
                />
                {actions.advanceRound && (
                  <button
                    type="button"
                    className="btn btn--primary"
                    data-testid="advance-round"
                    onClick={() => send({ type: 'advance_round' })}
                  >
                    {state.jeopardy.currentRound === 'JEOPARDY'
                      ? `Advance to ${DOUBLE_PERIL}`
                      : `Advance to ${FINAL_PERIL}`}
                  </button>
                )}
              </>
            )}
          </div>

          <div className="stack">
            <Scoreboard
              players={state.players}
              boardControlPlayerId={state.jeopardy.boardControlPlayerId}
              clueState={state.jeopardy.clueState}
            />
            {/* #232 (Game Night only): lightweight power-up activity log (mock 09). */}
            {powerupsOn && <PowerUpHostLog items={powerupLog} />}
            {actions.endGame && (
              <button
                type="button"
                className="btn btn--danger"
                data-testid="end-game"
                onClick={() => send({ type: 'end_game' })}
              >
                End game
              </button>
            )}
          </div>
        </div>
      )}

      {/* Host clue-rating panel (#201) — a distinct surface BELOW the board (D201.11),
          host-only, shown once play is underway when the host enabled rating at create
          time (host-local flag, D201.12) and this is not an auto-host game (no human host
          to rate). Never rendered on the player / presentation views. It self-hides until
          a clue has been completed. */}
      {state &&
        gamePhaseOf(state) !== 'LOBBY' &&
        gamePhaseOf(state) !== 'GENERATING' &&
        ticket &&
        guestToken &&
        loadRateCluesEnabled() &&
        !isAutoHost(state) && (
          <ClueRating state={state} ticket={ticket} guestToken={guestToken} />
        )}
    </div>
  );
}
